import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useArticlesCore } from '../../../../core/modules/articles';
import { useMeCore } from '../../../../core/modules/me';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useCompanyCore } from '../../../../core/modules/company';
import { FilterDrawer, FilterField } from '../../shared/FilterDrawer';
import { ProductCategory } from '../../../../core/interfaces/category.interface';

interface HeaderProps {
  initialSearch?: string;
  initialField?: 'code' | 'name';
  productTypes?: any[];
  productCategories?: any[];
}

interface ArticleFilters {
  code?: string;
  name?: string;
  product_type_id?: number;
  product_category_id?: number;
}

const Header = ({ initialSearch = '', initialField = 'code', productTypes = [], productCategories = [] }: HeaderProps) => {
  const { total, setOptions, clearError, clearArticle } = useArticlesCore();
  const { user } = useMeCore();
  const { companyId } = useCompanyCore();
  const { goToAddArticle } = useNavigationCore();

  const [filters, setFilters] = useState<ArticleFilters>({
    code: initialField === 'code' ? initialSearch : undefined,
    name: initialField === 'name' ? initialSearch : undefined,
    product_type_id: undefined,
    product_category_id: undefined,
  });

  // Sincronizar con valores iniciales cuando cambien
  useEffect(() => {
    if (initialSearch) {
      setFilters((prev) => ({
        ...prev,
        [initialField]: initialSearch,
      }));
    }
  }, [initialSearch, initialField]);

  const canWrite = useMemo(() => user && (user.privileges.superAdmin || user.privileges.writeProduct), [user]);

  // Cargar datos iniciales solo cuando se monte el componente
  useEffect(() => {
    if (companyId) {
      setOptions({});
    }
  }, [companyId, setOptions]);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    if (companyId) {
      const options: any = {};
      if (filters.code) {
        options.code = filters.code;
      }
      if (filters.name) {
        options.name = filters.name;
      }
      if (filters.product_type_id) {
        options.product_type_id = filters.product_type_id;
      }
      if (filters.product_category_id) {
        options.product_category_id = filters.product_category_id;
      }
      setOptions(options);
    }
  }, [companyId, filters.code, filters.name, filters.product_type_id, filters.product_category_id, setOptions]);

  const handleFiltersChange = useCallback((newFilters: ArticleFilters) => {
    setFilters(newFilters);
  }, []);

  const handleApplyFilters = useCallback((appliedFilters?: ArticleFilters) => {
    if (companyId && appliedFilters) {
      const options: any = {};
      if (appliedFilters.code) {
        options.code = appliedFilters.code;
      }
      if (appliedFilters.name) {
        options.name = appliedFilters.name;
      }
      if (appliedFilters.product_type_id) {
        options.product_type_id = appliedFilters.product_type_id;
      }
      if (appliedFilters.product_category_id) {
        options.product_category_id = appliedFilters.product_category_id;
      }
      setOptions(options);
    }
  }, [companyId, setOptions]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      code: undefined,
      name: undefined,
      product_type_id: undefined,
      product_category_id: undefined,
    });
    if (companyId) {
      setOptions({});
    }
  }, [companyId, setOptions]);

  const filterFields: FilterField[] = useMemo(() => [
    {
      key: 'code',
      label: 'Buscar por código',
      type: 'text',
      placeholder: 'Buscar por código',
    },
    {
      key: 'name',
      label: 'Buscar por descripción',
      type: 'text',
      placeholder: 'Buscar por nombre o descripción',
    },
    {
      key: 'product_type_id',
      label: 'Tipo de Producto',
      type: 'select',
      options: productTypes.map((type: any) => ({
        id: type.id,
        name: type.name || '',
        description: type.name,
      })),
    },
    {
      key: 'product_category_id',
      label: 'Categoría',
      type: 'category',
      categories: productCategories
        .filter((category: any) => 
          !filters.product_type_id || category.product_type_id === filters.product_type_id
        ) as ProductCategory[],
    },
  ], [productTypes, productCategories, filters.product_type_id]);

  const openCreateDialog = useCallback(() => {
    clearError();
    clearArticle(); // Limpiar el artículo del store para evitar que se carguen datos de edición previa
    goToAddArticle();
  }, [clearError, clearArticle, goToAddArticle]);

  return (
    <div className="p-24 sm:p-32 w-full border-b-1 bg-white header-components">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex flex-col items-center sm:items-start">
          <Typography className="text-24 md:text-32 font-extrabold tracking-tight leading-none">
            Articulos
          </Typography>
          <Typography className="text-14 font-medium" color="text.secondary">
            {total} articulo/s
          </Typography>
        </div>
        <div className="flex w-full sm:w-auto items-center gap-4 mt-4 sm:mt-0">
          <FilterDrawer
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            fields={filterFields}
            disabled={!companyId}
          />
          
          {canWrite && (
                <Button
                  variant="contained"
                  color="secondary"
                  className="my-auto mx-2"
                  disabled={!companyId}
                  onClick={openCreateDialog}
                  sx={{
                    minWidth: 120,
                    height: 48,
                    fontWeight: 700,
                    fontSize: 16,
                    borderRadius: 999,
                    boxShadow: '0 2px 8px 0 rgba(80,80,200,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AddCircleOutlineOutlinedIcon sx={{ fontSize: 22 }} />
                  <span className="mx-8">Crear</span>
                </Button>
              )}
        </div>
      </div>
    </div>
  );
};

export default Header;
